import { useEffect, useState } from "react";
import { Button } from "reactstrap";

import AppDrawer from "components/Drawers/AppDrawer";
import ActionConfigSection from "./ActionConfigSection/ActionConfigSection";
import ActionInfoSection from "./ActionInfoSection";
import AdditionalConfigSection from "./AdditionalConfigSection/AdditionalConfigSection";
import EditActionHomeSection from "./EditActionHomeSection";
import SelectActionSection from "./SelectActionSection";
import WorkflowUtils from "utils/WorkflowUtils";

const actionsWithoutAdditional = [
  "record-update-tags",
  "if-else-case",
  "record-update-fields",
];

const step = (step) => (
  <span className="font-weight-600 mr-2">Step {step}:</span>
);

/* Sections - Info, Action, ActionConfig, Additional,

   Next - The name of the next section (When user clicks next)
   Prev - The name of the previous section (When user clicks back)
   HeaderDescription - The description of the section
   NextValidation - Validation function the action must pass to be able to click next
*/
const sGuide = (isEditing, noAdditional = false) => ({
  First: "Action",
  Last: "Info",
  Action: {
    Next: "ActionConfig",
    Prev: null,
    HeaderDescription: <>{!isEditing && step(1)}Selecting an Action</>,
    NextValidation: (action) => !!action?.Type,
  },
  ActionConfig: {
    Next: "Additional",
    Prev: "Action",
    HeaderDescription: (
      <>{!isEditing && step(2)}Configuration for the selected Action</>
    ),
    NextValidation: (action) => WorkflowUtils.validateAction(action),
  },
  Additional: {
    Next: "Info",
    Prev: "ActionConfig",
    HeaderDescription: <>{!isEditing && step(3)}Additional Action settings</>,
  },
  Info: {
    Next: null,
    Prev: "Additional",
    HeaderDescription: (
      <>
        {!isEditing && step(noAdditional ? 3 : 4)}Name/Description of the new
        Action
      </>
    ),
    NextValidation: (action) => !!action?.Name,
  },
  EditHome: {
    HeaderDescription: "Choose a category you want to update",
  },
});

/**
 * WorkflowActionDrawer - Drawer that provides a list of workflow Actions for the user to select.
 *
 * @param {object} existingAction - Provide to enter edit mode for this Drawer. The existingAction to edit.
 */
const WorkflowActionsDrawer = ({
  open,
  nestedData, // Used for updating if-else-case Actions
  onClose,
  workflow,
  setActions,
  existingAction,
  handleDeleteAction,
}) => {
  const [action, setAction] = useState(null); // State for the action being created.
  const noAdditional = actionsWithoutAdditional?.includes(action?.Type);
  const sectionGuide = sGuide(!!existingAction, noAdditional); // Whether or not the action has additional steps in this drawer.
  const [section, setSection] = useState(
    existingAction ? "EditHome" : sectionGuide.First
  ); // The current section the Drawer is on.
  const [disabled, setDisabled] = useState(false);

  // EDIT MODE ONLY - Sync existing action with local action state.
  useEffect(() => {
    if (!existingAction) return;

    setSection("EditHome");
    setAction(JSON.parse(JSON.stringify(existingAction)));
  }, [existingAction]);

  const onExited = () => {
    setAction({});
    setSection(sectionGuide.First);
  };

  // For adding a nested action.
  const handleNestedAdd = (newAction) => {
    const { actionID, actionType } = nestedData;

    const newActions = WorkflowUtils.traverseActionsAndUpdate(
      workflow?.Actions,
      {
        Type: "AddToNested",
        id: actionID,
        NestedSide: actionType,
        NewAction: newAction,
      }
    );

    setActions(newActions);
    onClose();
  };

  const addAction = () => {
    const newAction = { ...action };

    delete newAction.ActionName;
    delete newAction.ActionDescription;

    if (nestedData) {
      return handleNestedAdd(newAction);
    }

    setActions([...workflow.Actions, newAction]);
    onClose();
  };

  const editAction = () => {
    setActions(
      WorkflowUtils.traverseActionsAndUpdate(workflow.Actions, {
        Type: "Update",
        id: action?.id,
        updatedAction: action,
      })
    );
    onClose();
  };

  const nextSection = () => {
    let nextSection = sectionGuide[section].Next;

    // Handle final step.
    if (!nextSection) {
      return addAction();
    }

    if (
      nextSection === "Additional" &&
      actionsWithoutAdditional.includes(action.Type)
    ) {
      nextSection = sectionGuide.Additional.Next;
    }

    setSection(nextSection);
  };

  const prevSection = () => {
    let prevSection = sectionGuide[section].Prev;

    if (
      prevSection === "Additional" &&
      actionsWithoutAdditional.includes(action.Type)
    ) {
      prevSection = sectionGuide.Additional.Prev;
    }

    setSection(prevSection);
  };

  const actionValidation = () => {
    let disabled = false;

    if (!action || !action?.FieldMapping?.length) return disabled;

    if (action?.Type === "record-create-update" && section !== "EditHome") {
      for (let fieldMap of action?.FieldMapping) {
        if (!fieldMap?.Source) return true;

        if (fieldMap?.Source === "File" && !fieldMap?.SourceKey) return true;

        if (
          (fieldMap?.Source === "Value" || fieldMap?.Source === "Dynamic") &&
          !fieldMap.Value
        )
          return true;

        if (fieldMap?.Source === "Action" && !fieldMap?.ActionID) return true;
      }
    }
    return disabled;
  };

  useEffect(() => {
    setDisabled(actionValidation());
  }, [action?.FieldMapping, action]);

  return (
    <AppDrawer
      open={open}
      onClose={
        existingAction && section !== "EditHome"
          ? () => setSection("EditHome")
          : onClose
      }
      onExited={onExited}
      headerTitle={existingAction ? "Updating Action" : "Action Creation"}
      headerDescription={sectionGuide[section].HeaderDescription}
      headerOptions={
        existingAction ? (
          <Button
            color="primary"
            size="sm"
            style={{ width: 100 }}
            disabled={disabled}
            onClick={
              section === "EditHome" ? editAction : () => setSection("EditHome")
            }
          >
            {section === "EditHome" ? "Update" : "Done"}
          </Button>
        ) : (
          <>
            <Button
              color="secondary"
              size="sm"
              style={{ width: 80 }}
              onClick={prevSection}
              disabled={sectionGuide[section].Prev === null}
            >
              Back
            </Button>
            <Button
              color="primary"
              size="sm"
              style={{ width: 100 }}
              onClick={nextSection}
              disabled={
                sectionGuide[section].NextValidation &&
                sectionGuide[section].NextValidation(action) === false
              }
            >
              {sectionGuide[section].Next ? "Next" : "Add Action"}
            </Button>
          </>
        )
      }
      bodyCSS="p-2"
      body={
        <>
          {section === "EditHome" && (
            <EditActionHomeSection
              setSection={setSection}
              noAdditional={noAdditional}
              handleDeleteAction={() => handleDeleteAction(existingAction?.id)}
            />
          )}
          {section === "Action" && (
            <SelectActionSection
              action={action}
              setAction={setAction}
              actions={workflow?.Actions}
            />
          )}
          {section === "ActionConfig" && (
            <ActionConfigSection
              action={action}
              setAction={setAction}
              workflow={workflow}
            />
          )}
          {section === "Additional" && (
            <AdditionalConfigSection
              action={action}
              setAction={setAction}
              nextSection={nextSection}
            />
          )}
          {section === "Info" && (
            <ActionInfoSection
              name={action?.Name || ""}
              description={action?.Description || ""}
              onChange={(e) =>
                setAction((prev) => ({
                  ...prev,
                  [e.target.name]: e.target.value,
                }))
              }
            />
          )}
        </>
      }
    />
  );
};

export default WorkflowActionsDrawer;
